import React, { useState, useMemo, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import collectionsHero from "../../assets/home/home.svg";
import collectionLogo from "../../assets/collection-icon.svg";
import {
  PlusIcon,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Trash2,
} from "lucide-react";
import { FaStar } from "react-icons/fa";
import axiosInstanceAuth from "../../apiInstances/axiosInstanceAuth";
import CollectionCreateModal from "./CollectionCreateModal";
import { useCollections } from "../../Hooks/useCollections/useCollections";
import Googlemap2 from "../common/GoogleMap/Googlemap2";
import CollectionPropertyCard from "./CollectionPropertyCard";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getDateRange(startDate, count) {
  const dates = [];
  const d = new Date(startDate);
  for (let i = 0; i < count; i++) {
    dates.push({
      date: d.getDate(),
      day: DAY_NAMES[d.getDay()],
      full: new Date(d),
    });
    d.setDate(d.getDate() + 1);
  }
  return dates;
}

function Collections() {
  const [activeTab, setActiveTab] = useState("properties");
  const [dateStripStart, setDateStripStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const [selectedDateKey, setSelectedDateKey] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [newCollectionDescription, setNewCollectionDescription] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [savedInspections, setSavedInspections] = useState([]);
  const [savedInspectionsLoading, setSavedInspectionsLoading] = useState(false);
  const [removingInspectionId, setRemovingInspectionId] = useState(null);
  const stripRef = useRef(null);
  const navigate = useNavigate();
  const FILTER_TABS = ["All", "Buy", "Rent", "Sold", "Inspected"];
  const [filter, setFilter] = useState("All");

  function getPropertyStatus(p) {
    const s = (
      p?.property?.status ||
      p?.propertyId?.status ||
      ""
    )
      .toString()
      .toLowerCase();
    if (s.includes("sold")) return "Sold";
    if (s.includes("active")) return "Buy";
    if (s.includes("rent") || s.includes("lease")) return "Rent";
    if (s.includes("inspect")) return "Inspected";
    return "Buy";
  }

  const {
    collections,
    loading: collectionsLoading,
    setCollections,
  } = useCollections();

  const toDateKey = (value) => {
    if (!value) return null;
    const d = typeof value === "string" ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) {
      return String(value).slice(0, 10);
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const formatSlotDate = (value) => {
    if (!value) return "";
    const d = typeof value === "string" ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString("en-AU", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  const formatSlotHeadingDate = (value) => {
    if (!value) return "";
    const d = typeof value === "string" ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString("en-AU", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  };

  const fetchSavedInspections = async () => {
    const token = localStorage.getItem("Token");
    if (!token) return;
    setSavedInspectionsLoading(true);
    try {
      const res = await axiosInstanceAuth.get("/savedInspection");
      const list = res?.data?.data ?? res?.data ?? [];
      setSavedInspections(Array.isArray(list) ? list : []);
    } catch {
      setSavedInspections([]);
    } finally {
      setSavedInspectionsLoading(false);
    }
  };

  const filteredProperties = useMemo(() => {
    if (!Array.isArray(savedInspections)) return [];
    return savedInspections.filter((p) => {
      const statusOk =
        filter === "All" ? true : getPropertyStatus(p) === filter;
      const sameDay =
        !selectedDateKey || toDateKey(p.show_date) === selectedDateKey;
      return statusOk && sameDay;
    });
  }, [savedInspections, filter, selectedDateKey]);

  const removeSavedInspection = async (id) => {
    setRemovingInspectionId(id);
    try {
      await axiosInstanceAuth.delete(`/savedInspection/${id}`);
      setSavedInspections((prev) => prev.filter((s) => (s._id ?? s.id) !== id));
    } catch (err) {
      alert(err?.response?.data?.message ?? "Failed to remove");
    } finally {
      setRemovingInspectionId(null);
    }
  };

  useEffect(() => {
    if (activeTab === "inspections") {
      fetchSavedInspections();
    }
  }, [activeTab]);

  const openCreateModal = () => {
    setEditingCollection(null);
    setNewCollectionName("");
    setNewCollectionDescription("");
    setCreateError("");
    setCreateModalOpen(true);
  };

  const openEditModal = (col) => {
    setEditingCollection(col);
    setNewCollectionName(col.name || "");
    setNewCollectionDescription(col.description || "");
    setCreateError("");
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    setEditingCollection(null);
    setNewCollectionName("");
    setNewCollectionDescription("");
    setCreateError("");
  };

  const handleSaveCollection = (createdOrUpdated) => {
    if (editingCollection && createdOrUpdated) {
      setCollections((prev) =>
        prev.map((c) =>
          c.id === editingCollection.id
            ? {
              ...c,
              name:
                createdOrUpdated.name ??
                (newCollectionName ? newCollectionName.trim() : ""),
              description:
                createdOrUpdated.description ??
                (newCollectionDescription
                  ? newCollectionDescription.trim()
                  : ""),
            }
            : c,
        ),
      );
    } else if (!editingCollection && createdOrUpdated) {
      const item = {
        id: createdOrUpdated.id ?? createdOrUpdated._id ?? `col-${Date.now()}`,
        name:
          createdOrUpdated.name ??
          (newCollectionName ? newCollectionName.trim() : ""),
        description:
          createdOrUpdated.description ??
          (newCollectionDescription ? newCollectionDescription.trim() : ""),
      };
      setCollections((prev) => [...prev, item]);
    }
    closeCreateModal();
  };

  const handleDeleteCollection = async (id) => {
    if (!window.confirm("Delete this collection?")) return;
    setDeletingId(id);
    try {
      await axiosInstanceAuth.delete(`/collection/${id}`);
      setCollections((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to delete collection";
      alert(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const openCollectionDetail = (e, col) => {
    if (e.target.closest("button")) return;
    navigate(`/collections/${col.id}`);
  };

  const dateStripDates = useMemo(
    () => getDateRange(new Date(dateStripStart), 21),
    [dateStripStart],
  );

  const scrollStrip = (dir) => {
    const step = 7;
    setDateStripStart((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + (dir === "next" ? step : -step));
      return next;
    });
  };

  return (
    <div className="bg-[#F3F6F9]">
      <div className="container mx-auto pb-9">
        <div className="inline-flex items-center gap-1 text-[#097FE6] font-medium hover:underline mb-8 mt-4 cursor-pointer">
          <ChevronLeft className="w-5 h-5" strokeWidth={2} />
          <span onClick={() => navigate("/dashboard")}>Back to dashboard</span>
        </div>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-[#000929] mb-2">
              Collections
            </h1>
          </div>
          <button
            type="button"
            onClick={openCreateModal}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#097FE6] px-5 py-3 font-medium text-white shadow-sm hover:bg-[#0664b7] transition-colors text-base"
          >
            <span>Create Collection</span>
            <PlusIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="inline-flex items-center gap-2 bg-[#E9EDF2] rounded-full px-1.5 py-1 mb-8 shadow-sm border border-gray-100">
          <button
            type="button"
            onClick={() => setActiveTab("properties")}
            className={`text-xs sm:text-sm rounded-full py-3 px-7 font-medium transition-colors ${activeTab === "properties"
              ? "bg-white shadow-sm text-[#000929]"
              : "text-gray-500 hover:bg-gray-100"
              }`}
          >
            Properties
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("inspections")}
            className={`text-xs sm:text-sm rounded-full py-3 px-7 font-medium transition-colors ${activeTab === "inspections"
              ? "bg-white shadow-sm text-[#000929]"
              : "text-gray-500 hover:bg-gray-100"
              }`}
          >
            Inspections
          </button>
        </div>

        {activeTab === "properties" && (
          <>
            <div className="flex flex-wrap gap-5">
              {collectionsLoading && collections.length === 0 ? (
                <>
                  {["1", "2", "3"].map((key) => (
                    <div
                      key={key}
                      className="bg-white rounded-3xl shadow-sm overflow-hidden border border-gray-100 w-full sm:max-w-[300px] xl:max-w-[325px] min-h-[306px] flex flex-col animate-pulse"
                      aria-hidden="true"
                    >
                      <div className="aspect-[4/3] overflow-hidden flex-shrink-0">
                        <div className="h-full w-full bg-gray-200" />
                      </div>
                      <div className="px-5 py-4 border-t border-gray-100">
                        <div className="h-6 bg-gray-200 rounded w-4/5" />
                      </div>
                    </div>
                  ))}
                </>
              ) : null}
              {collections.map((col) => (
                <div
                  key={col.id}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => openCollectionDetail(e, col)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && openCollectionDetail(e, col)
                  }
                  className="bg-white rounded-3xl shadow-sm overflow-hidden border border-gray-100 w-full sm:max-w-[300px] xl:max-w-[325px] min-h-[306px] flex flex-col group cursor-pointer hover:shadow-md transition-shadow"
                >
                  <div className="aspect-[4/3] overflow-hidden flex-shrink-0 relative flex items-center justify-center">
                    {
                      col.thumbnail ? (
                        <img
                          src={col.thumbnail}
                          alt="property_image"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <img src={collectionsHero} alt="property_image" className="w-24 h-24 " />
                      )
                    }
                    <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(col);
                        }}
                        className="p-2 rounded-full bg-white/90 shadow-sm text-gray-600 hover:bg-white hover:text-[#097FE6] transition-colors"
                        aria-label="Edit collection"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCollection(col.id);
                        }}
                        disabled={deletingId === col.id}
                        className="p-2 rounded-full bg-white/90 shadow-sm text-gray-600 hover:bg-white hover:text-red-600 transition-colors disabled:opacity-50"
                        aria-label="Delete collection"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="px-5 py-4 border-t border-gray-100">
                    <p className="text-xl font-medium text-[#000929]">
                      {col.name}
                    </p>
                  </div>
                </div>
              ))}

              <div
                role="button"
                tabIndex={0}
                onClick={() => navigate("/collections/nearby")}
                onKeyDown={(e) =>
                  e.key === "Enter" && navigate("/collections/nearby")
                }
                className="bg-[#E3E5E6] rounded-3xl shadow-sm border border-gray-100 flex flex-col justify-between w-full max-w-[325px] min-h-[306px] cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex-1 flex flex-col items-center justify-center px-6 text-center py-8">
                  <div className="mb-4 inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#F3F6FB]">
                    <FaStar className="text-[#097FE6]" size={16} />
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Tap the star on any property to add it to this Collection.
                  </p>
                </div>
                <div className="px-5 py-4 bg-white rounded-b-3xl border-t border-gray-100">
                  <p className="text-xl font-medium text-[#000929]">
                    Nearby me
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 sm:hidden">
              <button
                type="button"
                onClick={openCreateModal}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#097FE6] px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[#0664b7] transition-colors"
              >
                <span>Create Collection</span>
                <PlusIcon className="w-5 h-5" />
              </button>
            </div>
          </>
        )}

        {activeTab === "inspections" && (
          <>
            <div className="flex items-center gap-2 mb-6">
              <button
                type="button"
                onClick={() => scrollStrip("prev")}
                className="p-2 rounded-xl bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 shrink-0"
                aria-label="Previous dates"
              >
                <ChevronLeft
                  className="w-5 h-5 text-[#097FE6]"
                  strokeWidth={3}
                />
              </button>
              <div
                ref={stripRef}
                className="flex gap-1 overflow-x-auto scroll-smooth py-2 scrollbar-hide flex-1"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {dateStripDates.map((item) => {
                  const key = `${item.full.getFullYear()}-${String(
                    item.full.getMonth() + 1,
                  ).padStart(2, "0")}-${String(item.full.getDate()).padStart(
                    2,
                    "0",
                  )}`;
                  const isSelected = selectedDateKey === key;
                  const hasInspectionForDay = savedInspections.some(
                    (s) => toDateKey(s.show_date) === key,
                  );
                  const baseClasses =
                    "shrink-0 flex flex-col gap-1 items-center justify-center min-w-[70px] min-h-[90px] py-2 px-2 rounded-xl border text-center transition-colors";
                  const stateClasses = isSelected
                    ? "bg-[#097FE6] text-white border-gray-200 opacity-100"
                    : hasInspectionForDay
                      ? "bg-white text-[#000929] border-gray-200 opacity-100"
                      : "bg-transparent opacity-50 border-gray-100 hover:opacity-70";
                  return (
                    <button
                      type="button"
                      key={key}
                      onClick={() => setSelectedDateKey(key)}
                      className={`${baseClasses} ${stateClasses}`}
                    >
                      <span className="text-xl ">{item.date}</span>
                      <span className="text-base">{item.day}</span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => scrollStrip("next")}
                className="p-2 rounded-xl bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 shrink-0"
                aria-label="Next dates"
              >
                <ChevronRight
                  className="w-5 h-5 text-[#097FE6]"
                  strokeWidth={3}
                />
              </button>
            </div>
            <div className="rounded-3xl bg-[#E9EDF1] border border-gray-200 min-h-[430px] flex flex-col items-center gap-6 justify-center px-6 py-12 text-center">
              <div className="mb-5 flex items-center justify-center">
                <img src={collectionLogo} alt="Collections" className="w-14 h-14" />
              </div>
              <p className="text-[#000929] font-semibold text-base sm:text-3xl mb-6">
                Whenever you add an event to your <br /> plan, it'll show up here.                </p>
              <Link
                to="/collections/inspections"
                className="inline-flex items-center gap-2 rounded-full bg-[#097FE6] px-5 py-3 text-sm font-medium text-white shadow-sm hover:bg-[#0664b7] transition-colors"
              >
                See All Inspection Times
                <ChevronRight className="w-4 h-4 text-white" strokeWidth={3} />
              </Link>
            </div>
          </>
        )}
      </div>

      <CollectionCreateModal
        open={createModalOpen}
        onClose={closeCreateModal}
        editingCollection={editingCollection}
        onSaved={handleSaveCollection}
      />
    </div>
  );
}

export default Collections;
